// src/api/services/product.ts
import { api } from './index';

// Product interfaces
export interface Product {
  id: number;
  name: string;
  description?: string;
  seq: number;
}

export interface ProductCreateDTO {
  name: string;
  description?: string;
  seq?: number;
}

export interface ProductUpdateDTO {
  name?: string;
  description?: string;
  seq?: number;
}

// Query options interface
export interface QueryOptions {
  max?: number;
  offset?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  view?: string;
  additional?: string;
  [key: string]: any; // For custom filters
}

// Where query interface
export interface WhereQuery {
  where: string;
  params: Record<string, any>;
}

// Response interfaces
export interface ListResponse<T> {
  statusCode: number;
  data: T[];
  totalCount: number;
}

export interface SingleResponse<T> {
  statusCode: number;
  data: T;
}

// Product service
export const productService = {
  /**
   * List all products with optional pagination and sorting
   */
  list: (options?: QueryOptions) =>
    api.get<ListResponse<Product>>('/product', { params: options }),

  /**
   * Find products with complex filtering
   */
  where: (whereData: WhereQuery, options?: QueryOptions) =>
    api.post<ListResponse<Product>>('/product/where', whereData, { 
      params: options 
    }),

  /**
   * Get a single product by ID
   */
  read: (id: number, options?: Pick<QueryOptions, 'view' | 'additional'>) =>
    api.get<SingleResponse<Product>>(`/product/${id}`, { 
      params: options 
    }),

  /**
   * Create a new product
   */
  create: (data: ProductCreateDTO) =>
    api.post<SingleResponse<Product>>('/product', data),

  /**
   * Update an existing product
   */
  update: (id: number, data: ProductUpdateDTO) =>
    api.put<SingleResponse<Product>>(`/product/${id}`, data),

  /**
   * Delete a product
   */
  delete: (id: number) =>
    api.delete<{ statusCode: number }>(`/product/${id}`),

  listAll: async () => {
    let allProducts: Product[] = [];
    let offset = 0;
    const max = 99; // Maximum limit per request

    while (true) {
      const response = await productService.list({ max, offset });
      allProducts = allProducts.concat(response.data.data);

      if (response.data.data.length < max) {
        break; // Exit loop if fewer items than max were returned
      }

      offset += max; // Increment offset for the next batch
    }

    return allProducts;
  },
  
  /**
   * Helper method to find products by name
   */
  findByName: (name: string, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.name LIKE :name",
      params: { name: `%${name}%` }
    };
    return productService.where(whereData, options);
  },

  /**
   * Update the sequence of multiple products in one call
   */
  updateSequence: (productIds: number[]) =>
    api.post<{ statusCode: number }>('/product/update-sequence', { productIds }),
};